var __defProp = Object.defineProperty;
var __typeError = (msg) => {
  throw TypeError(msg);
};
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __publicField = (obj, key, value) => __defNormalProp(obj, typeof key !== "symbol" ? key + "" : key, value);
var __accessCheck = (obj, member, msg) => member.has(obj) || __typeError("Cannot " + msg);
var __privateGet = (obj, member, getter) => (__accessCheck(obj, member, "read from private field"), getter ? getter.call(obj) : member.get(obj));
var __privateAdd = (obj, member, value) => member.has(obj) ? __typeError("Cannot add the same private member more than once") : member instanceof WeakSet ? member.add(obj) : member.set(obj, value);
var __privateSet = (obj, member, value, setter) => (__accessCheck(obj, member, "write to private field"), setter ? setter.call(obj, value) : member.set(obj, value), value);
var __privateMethod = (obj, member, method) => (__accessCheck(obj, member, "access private method"), method);

// src/action-menu/action-menu.ts
import { Controller } from "@hotwired/stimulus";

// src/utilities.ts
function assert(condition, message) {
  if (!condition) throw new Error(message);
}

// src/action-menu/action-menu.ts
var _onTriggerClick, _onTriggerKeyDown, _onMenuKeyDown, _onMouseDown, _ActionMenuController_instances, getAdjacentItem_fn, getMatchingItem_fn;
var ActionMenuController = class extends Controller {
  constructor() {
    super(...arguments);
    __privateAdd(this, _ActionMenuController_instances);
    __privateAdd(this, _onTriggerClick, () => {
      if (this.isOpen) {
        this.close();
      } else {
        this.open();
      }
    });
    __privateAdd(this, _onTriggerKeyDown, (event) => {
      if ([" ", "Enter", "ArrowDown"].includes(event.key)) {
        event.preventDefault();
        this.open();
        this.itemTargets[0]?.focus();
      }
    });
    __privateAdd(this, _onMenuKeyDown, (event) => {
      if (["Enter", " "].includes(event.key)) return;
      event.preventDefault();
      const firstItem = this.itemTargets[0];
      const lastItem = this.itemTargets[this.itemTargets.length - 1];
      if (event.key === "Escape") {
        this.close();
      } else if (event.key === "Home") {
        firstItem?.focus();
      } else if (event.key === "End") {
        lastItem?.focus();
      } else if (event.key === "ArrowDown") {
        (__privateMethod(this, _ActionMenuController_instances, getAdjacentItem_fn).call(this, 1) ?? firstItem)?.focus();
      } else if (event.key === "ArrowUp") {
        (__privateMethod(this, _ActionMenuController_instances, getAdjacentItem_fn).call(this, -1) ?? lastItem)?.focus();
      } else {
        __privateMethod(this, _ActionMenuController_instances, getMatchingItem_fn).call(this, event.key)?.focus();
      }
    });
    __privateAdd(this, _onMouseDown, (event) => {
      if (!this.element.contains(event.target)) {
        this.close();
      }
    });
  }
  connect() {
    assert(this.triggerTarget.getAttribute("id"), "trigger must have a unique id");
    assert(this.menuTarget.getAttribute("id"), "menu must have a unique id");
    assert(this.menuTarget.getAttribute("role") === "menu", 'menu must have `role="menu"`');
    this.triggerTarget.setAttribute("aria-controls", this.menuTarget.id);
    this.triggerTarget.setAttribute("aria-haspopup", "true");
    this.triggerTarget.addEventListener("click", __privateGet(this, _onTriggerClick));
    this.triggerTarget.addEventListener("keydown", __privateGet(this, _onTriggerKeyDown));
    this.menuTarget.tabIndex = -1;
    this.menuTarget.setAttribute("aria-labelledby", this.triggerTarget.id);
    this.menuTarget.addEventListener("keydown", __privateGet(this, _onMenuKeyDown));
    document.addEventListener("mousedown", __privateGet(this, _onMouseDown));
  }
  disconnect() {
    this.element.classList.remove("is-open");
    this.triggerTarget.removeAttribute("aria-expanded");
    this.triggerTarget.removeEventListener("click", __privateGet(this, _onTriggerClick));
    this.triggerTarget.removeEventListener("keydown", __privateGet(this, _onTriggerKeyDown));
    this.menuTarget.removeEventListener("keydown", __privateGet(this, _onMenuKeyDown));
    document.addEventListener("mousedown", __privateGet(this, _onMouseDown));
  }
  itemTargetConnected(item) {
    assert(item.getAttribute("role") === "menuitem", 'items must have `role="menuitem"`');
    item.tabIndex = -1;
  }
  open() {
    this.element.classList.add("is-open");
    this.triggerTarget.setAttribute("aria-expanded", "true");
    this.menuTarget.focus();
  }
  close() {
    this.element.classList.remove("is-open");
    this.triggerTarget.removeAttribute("aria-expanded");
    this.triggerTarget.focus();
  }
  get isOpen() {
    return this.triggerTarget.getAttribute("aria-expanded") === "true";
  }
};
_onTriggerClick = new WeakMap();
_onTriggerKeyDown = new WeakMap();
_onMenuKeyDown = new WeakMap();
_onMouseDown = new WeakMap();
_ActionMenuController_instances = new WeakSet();
getAdjacentItem_fn = function(offset) {
  const current = this.itemTargets.indexOf(document.activeElement);
  if (current === -1) return null;
  const size = this.itemTargets.length;
  return this.itemTargets[(current + offset + size) % size];
};
getMatchingItem_fn = function(key) {
  const size = this.itemTargets.length;
  const current = this.itemTargets.indexOf(document.activeElement);
  for (let i = 0; i < this.itemTargets.length; i += 1) {
    const item = this.itemTargets[(current + i + 1) % size];
    const text = (item.textContent ?? "").charAt(0).toLowerCase();
    if (text === key) return item;
  }
  return null;
};
__publicField(ActionMenuController, "targets", ["trigger", "menu", "item"]);

// src/autocomplete/autocomplete.ts
import Combobox from "@github/combobox-nav";
import { Controller as Controller2 } from "@hotwired/stimulus";
var _combobox, _onKeyDown, _onFocus, _onInput, _onBlur, _onMouseDown2, _onCommit, _AutocompleteController_instances, setValue_fn;
var AutocompleteController = class extends Controller2 {
  constructor() {
    super(...arguments);
    __privateAdd(this, _AutocompleteController_instances);
    __privateAdd(this, _combobox);
    /**
     * Events
     */
    __privateAdd(this, _onKeyDown, (event) => {
      const isArrowKey = ["ArrowDown", "ArrowUp"].includes(event.key);
      if (isArrowKey && !this.openValue) {
        void this.query();
      }
    });
    __privateAdd(this, _onFocus, () => {
      this.searchTarget.select();
    });
    __privateAdd(this, _onInput, () => {
      __privateMethod(this, _AutocompleteController_instances, setValue_fn).call(this, "");
      void this.query();
    });
    __privateAdd(this, _onBlur, () => {
      this.close();
    });
    __privateAdd(this, _onMouseDown2, (event) => {
      event.preventDefault();
    });
    __privateAdd(this, _onCommit, (event) => {
      const option = event.target;
      const text = option.textContent ?? "";
      this.searchTarget.value = option.dataset.autocompleteOptionLabel ?? text;
      __privateMethod(this, _AutocompleteController_instances, setValue_fn).call(this, option.dataset.autocompleteOptionValue ?? text);
      this.close();
    });
  }
  connect() {
    __privateSet(this, _combobox, new Combobox(this.searchTarget, this.optionsTarget, {
      tabInsertsSuggestions: true,
      firstOptionSelectionMode: "selected"
    }));
    this.searchTarget.addEventListener("focus", __privateGet(this, _onFocus));
    this.searchTarget.addEventListener("input", __privateGet(this, _onInput));
    this.searchTarget.addEventListener("blur", __privateGet(this, _onBlur));
    this.searchTarget.addEventListener("keydown", __privateGet(this, _onKeyDown));
    this.optionsTarget.addEventListener("mousedown", __privateGet(this, _onMouseDown2));
    this.optionsTarget.addEventListener("combobox-commit", __privateGet(this, _onCommit));
  }
  disconnect() {
    this.element.classList.remove("is-open");
    this.searchTarget.removeEventListener("focus", __privateGet(this, _onFocus));
    this.searchTarget.removeEventListener("input", __privateGet(this, _onInput));
    this.searchTarget.removeEventListener("blur", __privateGet(this, _onBlur));
    this.searchTarget.removeEventListener("keydown", __privateGet(this, _onKeyDown));
    this.optionsTarget.removeEventListener("mousedown", __privateGet(this, _onMouseDown2));
    this.optionsTarget.removeEventListener("combobox-commit", __privateGet(this, _onCommit));
    __privateGet(this, _combobox)?.destroy();
    __privateSet(this, _combobox, void 0);
  }
  openValueChanged() {
    if (this.openValue) {
      __privateGet(this, _combobox)?.start();
      this.element.classList.add("is-open");
    } else {
      __privateGet(this, _combobox)?.stop();
      this.element.classList.remove("is-open");
    }
  }
  /**
   * Public API
   */
  async query() {
    await new Promise((resume) => {
      this.dispatch("query", {
        bubbles: true,
        detail: { resume, query: this.searchTarget.value },
        target: this.optionsTarget
      });
    });
    this.openValue = Boolean(this.optionsTarget.childElementCount);
    requestAnimationFrame(() => __privateGet(this, _combobox)?.resetSelection());
  }
  close() {
    this.openValue = false;
  }
};
_combobox = new WeakMap();
_onKeyDown = new WeakMap();
_onFocus = new WeakMap();
_onInput = new WeakMap();
_onBlur = new WeakMap();
_onMouseDown2 = new WeakMap();
_onCommit = new WeakMap();
_AutocompleteController_instances = new WeakSet();
setValue_fn = function(value) {
  if (value !== this.valueTarget.value) {
    this.valueTarget.value = value;
    this.dispatch("change", { bubbles: true, detail: { value } });
  }
};
__publicField(AutocompleteController, "targets", ["search", "value", "options"]);
__publicField(AutocompleteController, "values", {
  open: { type: Boolean, default: false }
});

// src/autocomplete/autocomplete-options.ts
import { Controller as Controller3 } from "@hotwired/stimulus";
var _onQuery;
var AutocompleteOptionsController = class extends Controller3 {
  constructor() {
    super(...arguments);
    __privateAdd(this, _onQuery, (event) => {
      void Promise.resolve(this.query(event.detail.query)).finally(() => event.detail.resume());
    });
  }
  connect() {
    this.element.addEventListener("autocomplete:query", __privateGet(this, _onQuery));
  }
  disconnect() {
    this.element.removeEventListener("autocomplete:query", __privateGet(this, _onQuery));
    this.element.replaceChildren();
  }
};
_onQuery = new WeakMap();

// src/autocomplete/autocomplete-fetch-options.ts
function isAbortError(error) {
  return error instanceof DOMException && error.name === "AbortError";
}
var _abortController, _AutocompleteFetchOptionsController_instances, fetchOptions_fn, abort_fn;
var AutocompleteFetchOptionsController = class extends AutocompleteOptionsController {
  constructor() {
    super(...arguments);
    __privateAdd(this, _AutocompleteFetchOptionsController_instances);
    __privateAdd(this, _abortController, new AbortController());
  }
  disconnect() {
    super.disconnect();
    __privateMethod(this, _AutocompleteFetchOptionsController_instances, abort_fn).call(this);
  }
  async query(query) {
    __privateMethod(this, _AutocompleteFetchOptionsController_instances, abort_fn).call(this);
    try {
      await __privateMethod(this, _AutocompleteFetchOptionsController_instances, fetchOptions_fn).call(this, query);
    } catch (error) {
      if (!isAbortError(error)) {
        throw error;
      }
    }
  }
};
_abortController = new WeakMap();
_AutocompleteFetchOptionsController_instances = new WeakSet();
fetchOptions_fn = async function(query) {
  const url = new URL(this.urlValue, window.location.href);
  url.searchParams.set("query", query);
  const response = await fetch(url, {
    signal: __privateGet(this, _abortController).signal,
    headers: { "Content-Type": "text/html" }
  });
  if (response.ok) {
    this.element.innerHTML = await response.text();
  } else {
    throw new Error(response.statusText);
  }
};
abort_fn = function() {
  __privateGet(this, _abortController).abort();
  __privateSet(this, _abortController, new AbortController());
};
__publicField(AutocompleteFetchOptionsController, "values", {
  url: String
});

// src/autocomplete/autocomplete-inline-options.ts
var _options;
var AutocompleteInlineOptionsController = class extends AutocompleteOptionsController {
  constructor() {
    super(...arguments);
    __privateAdd(this, _options, []);
  }
  connect() {
    super.connect();
    __privateSet(this, _options, Array.from(this.element.children));
    this.element.replaceChildren();
  }
  disconnect() {
    super.disconnect();
    this.element.replaceChildren(...__privateGet(this, _options));
  }
  query(query) {
    const pattern = new RegExp(query, "i");
    const options = __privateGet(this, _options).filter((option) => {
      const label = option.dataset.comboboxLabel ?? option.textContent ?? "";
      return pattern.test(label);
    });
    this.element.replaceChildren(...options);
  }
};
_options = new WeakMap();

// src/autosubmit/autosubmit.ts
import { Controller as Controller4 } from "@hotwired/stimulus";
function debounce(func, delay) {
  let timerId;
  return (...args) => {
    clearTimeout(timerId);
    timerId = setTimeout(() => func(...args), delay);
  };
}
var AutosubmitController = class extends Controller4 {
  constructor() {
    super(...arguments);
    __publicField(this, "submit", () => {
      if (this.hasSubmitterTarget) {
        this.element.requestSubmit(this.submitterTarget);
      } else {
        this.element.requestSubmit();
      }
    });
    __publicField(this, "submitLater", debounce(this.submit, 500));
  }
};
__publicField(AutosubmitController, "targets", ["submitter"]);

// src/class-list/class-list.ts
import { Controller as Controller5 } from "@hotwired/stimulus";
var _ClassListController_instances, classList_get;
var ClassListController = class extends Controller5 {
  constructor() {
    super(...arguments);
    __privateAdd(this, _ClassListController_instances);
  }
  add() {
    __privateGet(this, _ClassListController_instances, classList_get).add(this.nameValue);
  }
  remove() {
    __privateGet(this, _ClassListController_instances, classList_get).remove(this.nameValue);
  }
  toggle() {
    __privateGet(this, _ClassListController_instances, classList_get).toggle(this.nameValue);
  }
};
_ClassListController_instances = new WeakSet();
classList_get = function() {
  const selector = this.selectorValue;
  if (selector) {
    const [element, ...others] = document.querySelectorAll(selector);
    if (!element) throw new Error(`No such element: ${selector}`);
    if (others.length) throw new Error(`Multiple elements match: ${selector}`);
    return element.classList;
  }
  return this.element.classList;
};
__publicField(ClassListController, "values", {
  name: String,
  selector: String
});

// src/datepicker/datepicker.ts
import { Controller as Controller6 } from "@hotwired/stimulus";
import moment from "moment";
import Pikaday from "pikaday";
var _datepicker;
var DatepickerController = class extends Controller6 {
  constructor() {
    super(...arguments);
    __privateAdd(this, _datepicker);
  }
  connect() {
    assert(this.formatValue, "data-datepicker-format-value is required");
    __privateSet(this, _datepicker, new Pikaday({
      field: this.element,
      format: this.formatValue,
      toString(date, format) {
        return moment(date).format(format);
      },
      parse(value, format) {
        const date = moment(value, format);
        return date.isValid() ? date.toDate() : null;
      },
      i18n: {
        previousMonth: "Previous Month",
        nextMonth: "Next Month",
        months: [
          "January",
          "February",
          "March",
          "April",
          "May",
          "June",
          "July",
          "August",
          "September",
          "October",
          "November",
          "December"
        ],
        weekdays: ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"],
        weekdaysShort: ["S", "M", "T", "W", "T", "F", "S"]
      }
    }));
  }
  disconnect() {
    __privateGet(this, _datepicker)?.destroy();
  }
};
_datepicker = new WeakMap();
__publicField(DatepickerController, "values", {
  format: String
});

// src/mask/mask.ts
import { Controller as Controller7 } from "@hotwired/stimulus";
import { createAutoCorrectedDatePipe, createNumberMask } from "text-mask-addons";
import { maskInput } from "vanilla-text-mask";
var MASK_CONFIG = {
  cents: {
    guide: false,
    mask: [/\d/, /\d/]
  },
  currency: {
    guide: false,
    mask: createNumberMask({ prefix: "", allowDecimal: false })
  },
  currencyWithCents: {
    guide: false,
    mask: createNumberMask({ prefix: "", allowDecimal: true })
  },
  date: {
    guide: false,
    mask: [/\d/, /\d/, "/", /\d/, /\d/, "/", /\d/, /\d/, /\d/, /\d/],
    pipe: createAutoCorrectedDatePipe("mm/dd/yyyy")
  },
  phoneNumber: {
    guide: false,
    mask: [/[1-9]/, /\d/, /\d/, "-", /\d/, /\d/, /\d/, "-", /\d/, /\d/, /\d/, /\d/]
  },
  socialSecurityNumber: {
    guide: false,
    mask: [/\d/, /\d/, /\d/, "-", /\d/, /\d/, "-", /\d/, /\d/, /\d/, /\d/]
  },
  zip: {
    guide: false,
    mask: [/\d/, /\d/, /\d/, /\d/, /\d/]
  }
};
var MASK_TYPES = Object.keys(MASK_CONFIG);
var _mask;
var MaskController = class extends Controller7 {
  constructor() {
    super(...arguments);
    __privateAdd(this, _mask);
  }
  connect() {
    assert(this.typeValue, "data-mask-type-value is required");
    const config = MASK_CONFIG[this.typeValue];
    assert(config, `data-mask-type-value must be one of ${MASK_TYPES.join(", ")}`);
    __privateSet(this, _mask, maskInput({ inputElement: this.element, ...config }));
  }
  disconnect() {
    __privateGet(this, _mask)?.destroy();
  }
};
_mask = new WeakMap();
__publicField(MaskController, "values", {
  type: { type: String, default: void 0 }
});

// src/modal/modal.ts
import { Controller as Controller8 } from "@hotwired/stimulus";
import { createFocusTrap } from "focus-trap";
var _focusTrap, _ModalController_instances, dialog_get, _onDeactivate, _onKeyDown2, _onLegacyEvent;
var ModalController = class extends Controller8 {
  constructor() {
    super(...arguments);
    __privateAdd(this, _ModalController_instances);
    __privateAdd(this, _focusTrap);
    __privateAdd(this, _onDeactivate, () => {
      this.close();
    });
    __privateAdd(this, _onKeyDown2, (event) => {
      if (event.key === "Escape") {
        event.stopPropagation();
        event.preventDefault();
        this.close();
      } else {
        __privateGet(this, _onLegacyEvent).call(this, event);
      }
    });
    /**
     * We don't want style-closet-legacy to conflict with this modal implementation.
     *
     * When `.sc-Modal` or `.sc-Modal-close` receives a keyup or click event, we'll stop propagating
     * the event so style-closet-legacy never receives it.
     *
     * We'll do the same with `data-sc-modal-close`, but we'll also implement the close behavior
     * here to maintain backwards compatibility. This behavior will be deprecated at a later
     * point in time.
     */
    __privateAdd(this, _onLegacyEvent, (event) => {
      const target = event.target;
      if (target?.classList.contains("sc-Modal")) {
        event.stopPropagation();
      } else if (target?.classList.contains("sc-Modal-close")) {
        event.stopPropagation();
      } else if (target?.hasAttribute("data-sc-modal-close")) {
        event.stopPropagation();
        event.preventDefault();
        const isClick = event.type === "click";
        const isEnterOrSpaceKey = event instanceof KeyboardEvent && ["Enter", " "].includes(event.key);
        if (isClick || isEnterOrSpaceKey) {
          this.close();
        }
      }
    });
  }
  connect() {
    this.element.addEventListener("click", __privateGet(this, _onLegacyEvent));
    this.element.addEventListener("keydown", __privateGet(this, _onKeyDown2));
  }
  disconnect() {
    this.element.removeEventListener("click", __privateGet(this, _onLegacyEvent));
    this.element.removeEventListener("keydown", __privateGet(this, _onKeyDown2));
    __privateGet(this, _focusTrap)?.deactivate();
    __privateSet(this, _focusTrap, void 0);
  }
  openValueChanged() {
    __privateGet(this, _focusTrap) ?? __privateSet(this, _focusTrap, createFocusTrap(__privateGet(this, _ModalController_instances, dialog_get), {
      clickOutsideDeactivates: true,
      escapeDeactivates: true,
      onDeactivate: __privateGet(this, _onDeactivate)
    }));
    if (this.openValue) {
      this.element.classList.add("is-open");
      __privateGet(this, _focusTrap).activate();
    } else {
      this.element.classList.remove("is-open");
      __privateGet(this, _focusTrap).deactivate();
    }
  }
  open() {
    this.openValue = true;
  }
  close() {
    this.openValue = false;
  }
};
_focusTrap = new WeakMap();
_ModalController_instances = new WeakSet();
dialog_get = function() {
  const dialog = this.element.querySelector("[role=dialog]");
  assert(dialog, 'data-controller="modal" must contain an element with role="dialog"');
  return dialog;
};
_onDeactivate = new WeakMap();
_onKeyDown2 = new WeakMap();
_onLegacyEvent = new WeakMap();
__publicField(ModalController, "values", {
  open: { type: Boolean, default: false }
});

// src/modal-trigger/modal-trigger.ts
import { Controller as Controller9 } from "@hotwired/stimulus";
var ModalTriggerController = class extends Controller9 {
  open() {
    this.modalOutlet.open();
  }
  confirmSubmit(event) {
    const form = event.target;
    const modal = this.modalOutlet;
    const isValidModal = form.contains(modal.element);
    assert(isValidModal, "Your confirmation modal must appear within the form");
    const isDisabled = event.submitter && this.disabledTargets.includes(event.submitter);
    if (isDisabled) return;
    const isConfirmed = modal.openValue && modal.element.contains(event.submitter);
    if (isConfirmed) {
      this.modalOutlet.close();
    } else {
      event.preventDefault();
      event.stopImmediatePropagation();
      this.modalOutlet.open();
    }
  }
};
__publicField(ModalTriggerController, "outlets", ["modal"]);
__publicField(ModalTriggerController, "targets", ["disabled"]);

// src/tab-nav/tab-nav.ts
import { Controller as Controller10 } from "@hotwired/stimulus";
function isActiveItem(element) {
  return element.getAttribute("aria-selected") === "true";
}
var _onItemClick, _onItemKeyDown, _TabNavController_instances, activateDefaultItem_fn, activateRelativeItem_fn, getPanel_fn;
var TabNavController = class extends Controller10 {
  constructor() {
    super(...arguments);
    __privateAdd(this, _TabNavController_instances);
    __privateAdd(this, _onItemClick, (event) => {
      this.activateItem(event.currentTarget);
    });
    // https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Reference/Roles/tab_role#keyboard_interactions
    __privateAdd(this, _onItemKeyDown, (event) => {
      const item = event.currentTarget;
      if (event.key === "ArrowLeft") {
        event.preventDefault();
        __privateMethod(this, _TabNavController_instances, activateRelativeItem_fn).call(this, item, -1);
      } else if (event.key === "ArrowRight") {
        event.preventDefault();
        __privateMethod(this, _TabNavController_instances, activateRelativeItem_fn).call(this, item, 1);
      }
    });
  }
  itemTargetConnected(item) {
    assert(item.getAttribute("id"), "tab items must have an ID");
    assert(item.getAttribute("role") === "tab", 'tab items must have `role="tab"`');
    item.addEventListener("click", __privateGet(this, _onItemClick));
    item.addEventListener("keydown", __privateGet(this, _onItemKeyDown));
    __privateMethod(this, _TabNavController_instances, activateDefaultItem_fn).call(this);
  }
  itemTargetDisconnected(item) {
    item.removeEventListener("click", __privateGet(this, _onItemClick));
    item.removeEventListener("keydown", __privateGet(this, _onItemKeyDown));
    __privateMethod(this, _TabNavController_instances, activateDefaultItem_fn).call(this);
  }
  panelTargetConnected(panel) {
    assert(panel.getAttribute("id"), "tab panels must have an ID");
    assert(panel.getAttribute("role") === "tabpanel", 'tab panels must have `role="tabpanel"`');
    assert(panel.getAttribute("aria-labelledby"), "tab panels must have an `aria-labelledby`");
  }
  activateItem(activeItem) {
    this.itemTargets.forEach((item) => {
      const panel = __privateMethod(this, _TabNavController_instances, getPanel_fn).call(this, item);
      const isActive = item === activeItem;
      item.tabIndex = isActive ? 0 : -1;
      item.setAttribute("aria-selected", isActive.toString());
      item.setAttribute("aria-controls", panel.id);
      panel.tabIndex = 0;
      panel.hidden = !isActive;
    });
  }
};
_onItemClick = new WeakMap();
_onItemKeyDown = new WeakMap();
_TabNavController_instances = new WeakSet();
activateDefaultItem_fn = function() {
  const activeItem = this.itemTargets.find(isActiveItem) ?? this.itemTargets[0];
  if (activeItem) this.activateItem(activeItem);
};
activateRelativeItem_fn = function(item, offset) {
  const items = this.itemTargets;
  const index = items.indexOf(item);
  const relativeIndex = (index + offset + items.length) % items.length;
  const relativeItem = items[relativeIndex];
  if (relativeItem) {
    relativeItem.focus();
    this.activateItem(relativeItem);
  }
};
getPanel_fn = function(item) {
  const result = this.panelTargets.find((panel) => {
    const labelledBy = panel.getAttribute("aria-labelledby") ?? "";
    return labelledBy.split(/\s+/).includes(item.id);
  });
  assert(result, `Failed to locate a panel with 'aria-labelledby="${item.id}"'`);
  return result;
};
__publicField(TabNavController, "targets", ["item", "panel"]);

// src/tooltip/tooltip.ts
import { Controller as Controller11 } from "@hotwired/stimulus";
import Popper from "popper.js";
var _popper, _onKeyDown3, _onMouseLeave, _TooltipController_instances, createPopper_fn, renderTemplate_fn, removePopper_fn;
var TooltipController = class extends Controller11 {
  constructor() {
    super(...arguments);
    __privateAdd(this, _TooltipController_instances);
    __privateAdd(this, _popper);
    __publicField(this, "open", () => {
      this.openValue = true;
    });
    __publicField(this, "close", () => {
      this.openValue = false;
    });
    __publicField(this, "toggle", () => {
      this.openValue = !this.openValue;
    });
    __privateAdd(this, _onKeyDown3, (event) => {
      if (event.key === "Escape") {
        this.openValue = false;
      }
    });
    __privateAdd(this, _onMouseLeave, (event) => {
      const currentHover = event.relatedTarget;
      const isHoveringElement = this.element.contains(currentHover);
      const isHoveringTooltip = __privateGet(this, _popper)?.popper.contains(currentHover);
      if (!isHoveringElement && !isHoveringTooltip) {
        this.openValue = false;
      }
    });
  }
  connect() {
    this.element.addEventListener("focus", this.open);
    this.element.addEventListener("blur", this.close);
    this.element.addEventListener("mouseenter", this.open);
    this.element.addEventListener("mouseleave", __privateGet(this, _onMouseLeave));
    this.element.addEventListener("touchstart", this.toggle, { passive: true });
    document.addEventListener("keydown", __privateGet(this, _onKeyDown3));
  }
  disconnect() {
    __privateMethod(this, _TooltipController_instances, removePopper_fn).call(this);
    this.element.removeEventListener("focus", this.open);
    this.element.removeEventListener("blur", this.close);
    this.element.removeEventListener("mouseenter", this.open);
    this.element.removeEventListener("mouseleave", __privateGet(this, _onMouseLeave));
    this.element.removeEventListener("touchstart", this.toggle);
    document.removeEventListener("keydown", __privateGet(this, _onKeyDown3));
  }
  openValueChanged() {
    if (this.openValue) {
      __privateMethod(this, _TooltipController_instances, createPopper_fn).call(this);
    } else {
      __privateMethod(this, _TooltipController_instances, removePopper_fn).call(this);
    }
  }
};
_popper = new WeakMap();
_onKeyDown3 = new WeakMap();
_onMouseLeave = new WeakMap();
_TooltipController_instances = new WeakSet();
createPopper_fn = function() {
  __privateGet(this, _popper) ?? __privateSet(this, _popper, new Popper(this.element, __privateMethod(this, _TooltipController_instances, renderTemplate_fn).call(this), {
    placement: this.placementValue
  }));
  __privateGet(this, _popper).scheduleUpdate();
};
renderTemplate_fn = function() {
  const template = this.templateTarget.content;
  assert(template.firstElementChild, "tooltip template must contain a root element");
  assert(template.childElementCount < 2, "tooltip template contains more than one root element");
  const tooltip = template.firstElementChild.cloneNode(true);
  tooltip.addEventListener("mouseleave", __privateGet(this, _onMouseLeave));
  document.body.appendChild(tooltip);
  return tooltip;
};
removePopper_fn = function() {
  __privateGet(this, _popper)?.destroy();
  __privateGet(this, _popper)?.popper.remove();
  __privateSet(this, _popper, void 0);
};
__publicField(TooltipController, "targets", ["template"]);
__publicField(TooltipController, "values", {
  open: { type: Boolean, default: false },
  placement: { type: String, default: "top" }
});

// src/typed/typed.ts
import { Controller as Controller12 } from "@hotwired/stimulus";
import Typed from "typed.js";
var _typed, _onComplete;
var TypedController = class extends Controller12 {
  constructor() {
    super(...arguments);
    __privateAdd(this, _typed);
    __privateAdd(this, _onComplete, () => {
      const url = this.redirectValue;
      if (!url) return;
      const event = this.dispatch("redirect", {
        bubbles: true,
        detail: { url }
      });
      if (!event.defaultPrevented) {
        window.location.assign(url);
      }
    });
  }
  connect() {
    assert(this.messagesValue.length > 0, "`data-typed-messages-value` cannot be empty");
    __privateSet(this, _typed, new Typed(this.element, {
      strings: this.messagesValue,
      typeSpeed: this.speedValue,
      onComplete: __privateGet(this, _onComplete)
    }));
  }
  disconnect() {
    __privateGet(this, _typed)?.destroy();
    __privateSet(this, _typed, void 0);
  }
};
_typed = new WeakMap();
_onComplete = new WeakMap();
__publicField(TypedController, "values", {
  redirect: String,
  messages: Array,
  speed: { type: Number, default: 50 }
});

// src/index.ts
function registerSambaControllers(application) {
  application.register("action-menu", ActionMenuController);
  application.register("autocomplete", AutocompleteController);
  application.register("autocomplete-fetch-options", AutocompleteFetchOptionsController);
  application.register("autocomplete-inline-options", AutocompleteInlineOptionsController);
  application.register("autosubmit", AutosubmitController);
  application.register("class-list", ClassListController);
  application.register("datepicker", DatepickerController);
  application.register("mask", MaskController);
  application.register("modal", ModalController);
  application.register("modal-trigger", ModalTriggerController);
  application.register("tab-nav", TabNavController);
  application.register("tooltip", TooltipController);
  application.register("typed", TypedController);
}
export {
  ActionMenuController,
  AutocompleteController,
  AutocompleteFetchOptionsController,
  AutocompleteInlineOptionsController,
  AutocompleteOptionsController,
  AutosubmitController,
  ClassListController,
  DatepickerController,
  MaskController,
  ModalController,
  ModalTriggerController,
  TabNavController,
  TooltipController,
  TypedController,
  registerSambaControllers
};
//# sourceMappingURL=index.js.map